import * as React from 'react';
import {connect, useSelector} from 'react-redux';

import moment from 'moment';
import type {Dispatch} from 'redux';
import {change, formValueSelector} from 'redux-form';

import {serializeFilters} from 'nutshell-core';

import {Assignables} from 'nutshell-core/assignables';
import {Competitors} from 'nutshell-core/competitors';
import {Markets} from 'nutshell-core/markets';
import {Routing} from 'nutshell-core/routing';
import {Session} from 'nutshell-core/session';
import {Sources} from 'nutshell-core/sources';
import {Stagesets} from 'nutshell-core/stagesets';
import type {
    Assignable,
    Stageset,
    Source,
    Market,
    Competitor,
    CoreEntityType,
} from 'nutshell-core/types';

import {analyticsClient} from '../../../analytics-client';
import type {NutshellState} from '../../../store';

import * as createNewActions from '../../creator/creator-actions';
import {useGetSelectedFields} from '../../creator/customize-fields/use-get-selected-fields';
import type {FlashMessage} from '../../creator/types';
// @ts-expect-error
import * as dashboardSelectors from '../../master-dashboard/dashboard-selectors';

import {handleFormSubmissionError} from '../handle-form-submission-error';

import {getLinksForSubmission} from './helpers';
import {
    LeadFormConnected,
    LEADS_FORM_KEY,
    IS_CREATING_SINGLE_LEAD_FROM_MULTIPLE,
    NUM_LEADS_CREATING,
    RELATED_ENTITY,
    CREATOR_ORIGIN_ID,
    MASS_SELECT_OPTIONS,
} from './lead-form-connected';
import type {MassSelectOptions} from './lead-form-connected';

type OwnProps = {
    onSaveCallback: (entity: any, jumpToUrl: boolean) => any;
    onCancelClick: () => void;
};

type StateProps = {
    initialValues: any;
    enableReinitialize: boolean;
    keepDirtyOnReinitialize: boolean;
    usersAndTeams: Assignable[];
    stagesets: Stageset[];
    sources: Source[];
    markets: Market[];
    competitors: Competitor[];
    currentMarket: Market | null | undefined;
    selectedContacts: CoreEntityType[] | null | undefined;
    selectedAccounts: CoreEntityType[] | null | undefined;
    bulkCreateFields: any;
    massSelectOptions: MassSelectOptions;
};

type DispatchProps = {
    pushFlashMessage: (arg1: FlashMessage) => void;
    onSubmit: (arg1: any) => any;
    onSubmitSuccess: (arg1: any) => any;
    onSubmitFail: (errors: any[], dispatchFunction: Dispatch<any>, jqXHRError: any) => any;
};

type Props = OwnProps & StateProps & DispatchProps;

const mapStateToProps = function (state: NutshellState): StateProps {
    const selector = formValueSelector(LEADS_FORM_KEY);

    return {
        // Used by redux-form
        initialValues: {
            confidence: null,
            links: {
                productMaps: [],
                // TODO: This seems to take a while to populate, and is incomplete when first loading sometimes.
                owner: Session.getSessionStateUser(state),
                market: Markets.getDefaultMarket(state),
                contacts: NutClientConfig.hasEnhancedLeadCreateModal ? [] : null,
            },
        },
        // The initial value for assignee is initially empty when the page first loads,
        // and is only fully populated after a rest request to the backend.
        // By enabling reinitialization, we can update the initial value for assingee
        // after that response comes back.
        enableReinitialize: true,
        // In case the user has made any changes to the form before the session
        // user response came back, this will keep their changes and only change
        // the assignee if it's pristine.
        keepDirtyOnReinitialize: true,
        currentMarket: selector(state, 'links.market'),
        // @ts-expect-error
        usersAndTeams: Assignables.getAllEnabled(state),
        stagesets: Stagesets.getAll(state),
        sources: Sources.getAll(state),
        markets: Markets.getAll(state),
        competitors: Competitors.getAll(state),
        // Entities selected within the modal currently
        selectedContacts: selector(state, 'links.contacts'),
        selectedAccounts: selector(state, 'links.accounts'),
        bulkCreateFields: selector(
            state,
            NUM_LEADS_CREATING,
            IS_CREATING_SINGLE_LEAD_FROM_MULTIPLE,
            RELATED_ENTITY,
            CREATOR_ORIGIN_ID
        ),
        massSelectOptions: selector(state, MASS_SELECT_OPTIONS),
    };
};

// @ts-expect-error
function mapDispatchToProps(dispatch, ownProps: OwnProps): DispatchProps {
    return {
        // Not actually using dispatch yet, this is a temp solution
        onSubmit: (data: any) => {
            const lead: any = {
                customFields: data.customFields,
                description: data.description,
                priority: data.priority,
                dueTime: data.dueTime ? moment(new Date(data.dueTime)).unix() : undefined,
                links: getLinksForSubmission(data.links, 'leads'),
            };

            if (typeof data.confidence === 'number') {
                lead.confidence = data.confidence;
            }

            if (data.manualValue != null) {
                lead.manualValue = String(data.manualValue);
            }

            // Url and data for creating a single lead
            let requestUrl = '/rest/leads';
            let requestData = lead;

            // Fields relevant when creating leads from list
            const isCreatingSingleLeadFromMultiple = data[IS_CREATING_SINGLE_LEAD_FROM_MULTIPLE];
            const numLeadsCreating = data[NUM_LEADS_CREATING];
            const relatedEntity = data[RELATED_ENTITY];

            // Check if bulk creating leads
            if (!isCreatingSingleLeadFromMultiple && numLeadsCreating > 1) {
                requestUrl = '/rest/leads/bulk-create';

                if (data.massSelectOptions && data.massSelectOptions.isMassSelect) {
                    const massSelectParams = {
                        q: data.massSelectOptions.q,
                        filter: serializeFilters(data.massSelectOptions.filters),
                        omittedItems: data.massSelectOptions.omittedItems.join(','),
                        entityType: relatedEntity,
                    } as const;

                    requestUrl += `?${Routing.param(massSelectParams)}`;
                } else {
                    // Get the entity ids of the related entity (either 'contacts' or 'accounts')
                    const entityIds = data.links[relatedEntity]
                        ? // @ts-expect-error
                          data.links[relatedEntity].map((entity) => entity.id)
                        : [];

                    const requestParams = {
                        selectedIds: entityIds.join(','),
                        entityType: relatedEntity,
                    } as const;

                    requestUrl += `?${Routing.param(requestParams)}`;
                }

                // If creating multiple leads, the main relatedEntity is either 'contacts' or 'accounts' depending
                // on which list the leads are being created from. The main relatedEntity field cannot be changed.
                // The other associated related entity field, is optional and essentially a boolean field. If the
                // main relatedEntity is 'accounts', then the user may optionally select that the leads be created
                // with the associated related entity 'account', and vice versa.
                const associateRelatedEntity =
                    relatedEntity === 'contacts'
                        ? Boolean(data.relatedEntityAccount)
                        : Boolean(data.relatedEntityContact);

                // Creating multiple leads, one for each contact/account, so empty out the linked entities
                lead.links.contacts.length = 0;
                lead.links.accounts.length = 0;

                // Update request data for bulk creating leads
                requestData = {
                    lead,
                    associateRelatedEntity,
                };
            }

            // Solely for tracking purposes
            if (isCreatingSingleLeadFromMultiple) {
                const eventName =
                    relatedEntity === 'contacts'
                        ? analyticsClient.EVENTS.TRACK_BULK_LEAD_ASSOCIATION_PEOPLE_LIST
                        : analyticsClient.EVENTS.TRACK_BULK_LEAD_ASSOCIATION_COMPANIES_LIST;

                analyticsClient.track(eventName);
            }

            return (
                $.ajax({
                    url: requestUrl,
                    type: 'POST',
                    dataType: 'json',
                    contentType: 'application/json',
                    data: JSON.stringify({
                        data: requestData,
                    }),
                })
                    // Add the jqXHR object and lead data to the response, so that it can
                    // be used in the onSubmitSuccess handler
                    .then((response, status, jqXHR) => {
                        return {response, jqXHR, lead};
                    })
                    .fail(() => {
                        // TODO: Figure out how we want to display errors to the user. This
                        // will almost certainly involve looking at that actual jqXHR object,
                        // but we need to work with that object here and return out something
                        // that redux form is okay with. Because, the jqXHR object causes a
                        // 'Maximum call stack size exceeded error'
                        return 'Error saving';
                    })
            );
        },
        onSubmitSuccess: ({response, jqXHR}: any) => {
            if (jqXHR.status !== 204) {
                // Response contains the originId when multiple leads are created
                if (response.originId) {
                    // Set the internal field of creatorOriginId to the originId returned in the response the connected
                    // form component will use this to redirect to the leads page with this filter applied.
                    dispatch(change(LEADS_FORM_KEY, CREATOR_ORIGIN_ID, response.originId));
                }

                // Response contains the single lead created when creating a single lead
                if (response.leads && response.leads[0]) {
                    // Save callback, pass the newly created lead
                    ownProps.onSaveCallback(response.leads[0], true);
                }
            }
        },
        onSubmitFail: (errors, dispatchFunction, jqXHRError: any) => {
            handleFormSubmissionError(dispatch, jqXHRError, errors);
        },
        pushFlashMessage: (flashMessage: FlashMessage) => {
            dispatch(createNewActions.pushFlashMessage(flashMessage));
        },
    };
}

export function LeadFormComponent(props: Props) {
    const leadsSchema = useSelector((state: NutshellState) =>
        dashboardSelectors.getLeadsSchema(state)
    );

    const {fields, loading: isLoading} = useGetSelectedFields('LEAD');

    return (
        <LeadFormConnected
            // @ts-expect-error
            schema={leadsSchema}
            selectedFields={fields}
            isLoading={isLoading}
            onSaveCallback={props.onSaveCallback}
            initialValues={props.initialValues}
            usersAndTeams={props.usersAndTeams}
            stagesets={props.stagesets}
            sources={props.sources}
            markets={props.markets}
            competitors={props.competitors}
            currentMarket={props.currentMarket}
            onCancelClick={props.onCancelClick}
            onSubmit={props.onSubmit}
            onSubmitSuccess={props.onSubmitSuccess}
            onSubmitFail={props.onSubmitFail}
            enableReinitialize={props.enableReinitialize}
            keepDirtyOnReinitialize={props.keepDirtyOnReinitialize}
            pushFlashMessage={props.pushFlashMessage}
            bulkCreateFields={props.bulkCreateFields}
            numSelectedContacts={props.selectedContacts ? props.selectedContacts.length : 0}
            numSelectedAccounts={props.selectedAccounts ? props.selectedAccounts.length : 0}
            massSelectOptions={props.massSelectOptions}
        />
    );
}

// @ts-expect-error
const connector = connect<Props, OwnProps, _, _, _, _>(mapStateToProps, mapDispatchToProps);
export const LeadForm = connector(LeadFormComponent);
